import apiClient from '@/api/apiClient';
import type {
  EntityMonitoringStatusResponse,
  PulseAccountRunsResponse,
  PulseRunCategoriesResponse,
  PulseCategoryBreakdown,
  PulseRunCategoryBreakdownResponse,
  PulseRunChecksResponse,
  PulseRunEntitiesResponse,
  PulseAccountStatusResponse,
  PulseScheduledCategoriesResponse,
  PulseScheduledCategoryBreakdownResponse,
  PulseScheduledChecksResponse,
  PulseScheduledDayCheckItem,
  PulseScheduledEntitiesResponse,
  PulseScheduledPeriodDays,
  PulseEntityAggregatesResponse,
  PulseEntityEventCountsResponse,
  PulseEntityScheduleResponse,
  PulseEventsFilterOptionsResponse,
  PulseEventsSummary,
  PulseEventsResponse,
  PulseActivePoliciesResponse,
  PulsePolicyConfigurationResponse,
  PulsePolicyPeriodDays,
  PulsePolicySharedEntitiesResponse,
  PulsePolicySummaryResponse,
} from '@/types/entityPulse';
import type { DateString } from '@/types/date';
import type { CsvExportResponse } from '@/types/globalPolicyRisk';

/** Server-side filters shared by the entity and the account-wide pulse events lists. */
export interface PulseEventsFilters {
  /** Exact CPF / CNPJ (any format; a CNPJ matches its root). */
  primaryEntityTaxIds?: string[];
  targetEntityTaxIds?: string[];
  /** Exact names (case and spacing ignored). */
  primaryEntityNames?: string[];
  targetEntityNames?: string[];
  primaryEntityTaxIdTypes?: string[];
  eventSlugs?: string[];
  eventCategorySlugs?: string[];
  checkSlugs?: string[];
  policyIds?: string[];
  relationshipTypes?: string[];
  /** Monitoring categories of the checks (config category_slug). */
  checkCategorySlugs?: string[];
  /** 'own' (on the monitored entity itself) and/or 'related' (on its related parties). */
  eventScopes?: PulseEventScope[];
  /** Only the events found by these runs / checks (entity run history). */
  runIds?: string[];
  checkBlockIds?: string[];
  dateFrom?: DateString;
  dateTo?: DateString;
}

export type PulseEventScope = 'own' | 'related';

/** Entity page: 'own' = events found by monitoring the entity, 'others' = events on the entity found by
 * another entity's monitoring, 'all' = both. */
export type PulseEntityScope = 'own' | 'others' | 'all';

export interface PulseEventsPageParams extends PulseEventsFilters {
  limit: number;
  offset: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

const toPulseEventsFilterQuery = (filters: PulseEventsFilters) => ({
  primary_entity_tax_ids: filters.primaryEntityTaxIds,
  target_entity_tax_ids: filters.targetEntityTaxIds,
  primary_entity_names: filters.primaryEntityNames,
  target_entity_names: filters.targetEntityNames,
  primary_entity_tax_id_types: filters.primaryEntityTaxIdTypes,
  event_slugs: filters.eventSlugs,
  event_category_slugs: filters.eventCategorySlugs,
  check_slugs: filters.checkSlugs,
  policy_ids: filters.policyIds,
  relationship_types: filters.relationshipTypes,
  check_category_slugs: filters.checkCategorySlugs,
  event_scopes: filters.eventScopes,
  run_ids: filters.runIds,
  check_block_ids: filters.checkBlockIds,
  date_from: filters.dateFrom,
  date_to: filters.dateTo,
});

const toPulseEventsQuery = (params: PulseEventsPageParams) => ({
  limit: params.limit,
  offset: params.offset,
  sort_field: params.sortField,
  sort_order: params.sortOrder,
  ...toPulseEventsFilterQuery(params),
});

/** Which verificações the run / schedule lists count (see PulseChecksFilterState). */
export interface PulseCheckScopeParams {
  checkSlugs?: string[];
  checkCategorySlugs?: string[];
  entityTaxId?: string | null;
  entityTaxIdTypes?: string[];
}

const toCheckScopeQuery = (params: PulseCheckScopeParams) => ({
  check_slugs: params.checkSlugs?.length ? params.checkSlugs : undefined,
  check_category_slugs: params.checkCategorySlugs?.length ? params.checkCategorySlugs : undefined,
  entity_tax_id: params.entityTaxId || undefined,
  entity_tax_id_types: params.entityTaxIdTypes?.length ? params.entityTaxIdTypes : undefined,
});

/** The Verificações realizadas tab's filters and sort (its run list, categories and CSV). */
export interface PulseAccountRunsParams extends PulseCheckScopeParams {
  periodDays: PulsePolicyPeriodDays;
  policyIds?: string[];
  onlyWithEvents?: boolean;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

const toPulseAccountRunsQuery = (params: PulseAccountRunsParams) => ({
  ...toCheckScopeQuery(params),
  period_days: params.periodDays,
  policy_ids: params.policyIds?.length ? params.policyIds : undefined,
  only_with_events: params.onlyWithEvents,
  sort_field: params.sortField,
  sort_order: params.sortOrder,
});

/** The Verificações programadas tab's filters and sort (its list, categories and CSV). */
export interface PulseScheduledChecksParams extends PulseCheckScopeParams {
  periodDays: PulseScheduledPeriodDays;
  policyIds?: string[];
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
}

const toPulseScheduledChecksQuery = (params: PulseScheduledChecksParams) => ({
  ...toCheckScopeQuery(params),
  period_days: params.periodDays,
  policy_ids: params.policyIds?.length ? params.policyIds : undefined,
  sort_field: params.sortField,
  sort_order: params.sortOrder,
});

const entityPulseApi = {
  getAccountEvents(params: PulseEventsPageParams): Promise<PulseEventsResponse> {
    return apiClient.get('/entity-pulse/events', { params: toPulseEventsQuery(params) });
  },
  getAccountEventsFilterOptions(): Promise<PulseEventsFilterOptionsResponse> {
    return apiClient.get('/entity-pulse/filter-options');
  },
  clearAccountEventsCache(): Promise<void> {
    return apiClient.post('/entity-pulse/events/clear-cache', {});
  },
  getAccountEntityAggregates(params: PulseEventsPageParams & { sortCategory?: string }): Promise<PulseEntityAggregatesResponse> {
    return apiClient.get('/entity-pulse/entity-aggregates', {
      params: { ...toPulseEventsQuery(params), sort_category: params.sortCategory },
    });
  },
  /** A signed CSV link with every monitored entity of the entity aggregates (same filters, all pages). */
  exportAccountEntityAggregates(
    params: PulseEventsFilters & { sortField?: string; sortOrder?: 'asc' | 'desc'; sortCategory?: string },
  ): Promise<CsvExportResponse> {
    return apiClient.get('/entity-pulse/entity-aggregates/export', {
      params: {
        ...toPulseEventsFilterQuery(params),
        sort_field: params.sortField,
        sort_order: params.sortOrder,
        sort_category: params.sortCategory,
      },
    });
  },
  /** A signed CSV link with every account event of the events list (same filters and order, all pages). */
  exportAccountEvents(
    params: PulseEventsFilters & { sortField?: string; sortOrder?: 'asc' | 'desc' },
  ): Promise<CsvExportResponse> {
    return apiClient.get('/entity-pulse/events/export', {
      params: { ...toPulseEventsFilterQuery(params), sort_field: params.sortField, sort_order: params.sortOrder },
    });
  },
  /** The account's run history (one row per run, policy and check frequency), paged, newest first. */
  getAccountRuns(params: PulseAccountRunsParams & { limit: number; offset: number }): Promise<PulseAccountRunsResponse> {
    return apiClient.get('/entity-pulse/runs', {
      params: { ...toPulseAccountRunsQuery(params), limit: params.limit, offset: params.offset },
    });
  },
  /** The account's verificações realizadas in the period, one row per monitoring category (same filters). */
  getRunCategories(params: PulseAccountRunsParams): Promise<PulseRunCategoriesResponse> {
    return apiClient.get('/entity-pulse/runs/categories', { params: toPulseAccountRunsQuery(params) });
  },
  /** A page of one category's verificações realizadas, by check, policy, checked entity or run day (same filters). */
  getRunCategoryBreakdown(params: PulseAccountRunsParams & {
    categorySlug: string;
    breakdown: PulseCategoryBreakdown;
    limit: number;
    offset: number;
  }): Promise<PulseRunCategoryBreakdownResponse> {
    return apiClient.get(
      `/entity-pulse/runs/categories/${encodeURIComponent(params.categorySlug)}/${params.breakdown}`,
      { params: { ...toPulseAccountRunsQuery(params), limit: params.limit, offset: params.offset } },
    );
  },
  /** A signed CSV link with every row of the account's run history (same filters, all pages). */
  exportAccountRuns(params: PulseAccountRunsParams): Promise<CsvExportResponse> {
    return apiClient.get('/entity-pulse/runs/export', { params: toPulseAccountRunsQuery(params) });
  },
  /** The account's scheduled verificações (one row per due date and policy, earliest first), paged. */
  /** The scheduled verificações of the window, one row per monitoring category (same filters). */
  getScheduledCategories(params: PulseScheduledChecksParams): Promise<PulseScheduledCategoriesResponse> {
    return apiClient.get('/entity-pulse/scheduled-checks/categories', { params: toPulseScheduledChecksQuery(params) });
  },
  /** A page of one category's scheduled verificações, by check, policy, entity to check or due day (same filters). */
  getScheduledCategoryBreakdown(params: PulseScheduledChecksParams & {
    categorySlug: string;
    breakdown: PulseCategoryBreakdown;
    limit: number;
    offset: number;
  }): Promise<PulseScheduledCategoryBreakdownResponse> {
    return apiClient.get(
      `/entity-pulse/scheduled-checks/categories/${encodeURIComponent(params.categorySlug)}/${params.breakdown}`,
      { params: { ...toPulseScheduledChecksQuery(params), limit: params.limit, offset: params.offset } },
    );
  },
  getScheduledChecks(
    params: PulseScheduledChecksParams & { limit: number; offset: number },
  ): Promise<PulseScheduledChecksResponse> {
    return apiClient.get('/entity-pulse/scheduled-checks', {
      params: { ...toPulseScheduledChecksQuery(params), limit: params.limit, offset: params.offset },
    });
  },
  /** A signed CSV link with every row of the scheduled verificações (same filters and order, all pages). */
  exportScheduledChecks(params: PulseScheduledChecksParams): Promise<CsvExportResponse> {
    return apiClient.get('/entity-pulse/scheduled-checks/export', { params: toPulseScheduledChecksQuery(params) });
  },
  /** The verificações one policy has due on one date (all of them: at most the policy's checks). */
  getScheduledDayChecks(params: PulseCheckScopeParams & {
    policyId: string;
    dueDate: string;
  }): Promise<{ items: PulseScheduledDayCheckItem[]; fetched_at: string | null }> {
    return apiClient.get('/entity-pulse/scheduled-checks/day-checks', {
      params: { ...toCheckScopeQuery(params), policy_id: params.policyId, due_date: params.dueDate },
    });
  },
  /** The entities one policy has verificações due for on one date, paged. */
  getScheduledEntities(params: PulseCheckScopeParams & {
    policyId: string;
    dueDate: string;
    limit: number;
    offset: number;
  }): Promise<PulseScheduledEntitiesResponse> {
    return apiClient.get('/entity-pulse/scheduled-checks/entities', {
      params: {
        ...toCheckScopeQuery(params),
        policy_id: params.policyId,
        due_date: params.dueDate,
        limit: params.limit,
        offset: params.offset,
      },
    });
  },
  getAccountStatus(): Promise<PulseAccountStatusResponse> {
    return apiClient.get('/entity-pulse/account-status');
  },
  /** The verificações one run of a policy executed (only on one entity, with `taxId`), paged. */
  getRunChecks(params: PulseCheckScopeParams & {
    runId: string;
    policyId: string;
    taxId?: string;
    limit: number;
    offset: number;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PulseRunChecksResponse> {
    return apiClient.get(
      `/entity-pulse/runs/${encodeURIComponent(params.runId)}/policies/${encodeURIComponent(params.policyId)}/checks`,
      {
        params: {
          ...toCheckScopeQuery(params),
          tax_id: params.taxId,
          limit: params.limit,
          offset: params.offset,
          sort_field: params.sortField,
          sort_order: params.sortOrder,
        },
      },
    );
  },
  /** The entities one run of a policy checked (only by one verificação, with `checkBlockId`), paged. */
  getRunEntities(params: PulseCheckScopeParams & {
    runId: string;
    policyId: string;
    checkBlockId?: string;
    limit: number;
    offset: number;
    sortField?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<PulseRunEntitiesResponse> {
    return apiClient.get(
      `/entity-pulse/runs/${encodeURIComponent(params.runId)}/policies/${encodeURIComponent(params.policyId)}/entities`,
      {
        params: {
          ...toCheckScopeQuery(params),
          check_block_id: params.checkBlockId,
          limit: params.limit,
          offset: params.offset,
          sort_field: params.sortField,
          sort_order: params.sortOrder,
        },
      },
    );
  },
  getActivePolicies(): Promise<PulseActivePoliciesResponse> {
    return apiClient.get('/entity-pulse/policies');
  },
  getPolicySharedEntities(policyId: string): Promise<PulsePolicySharedEntitiesResponse> {
    return apiClient.get(`/entity-pulse/policies/${encodeURIComponent(policyId)}/shared-entities`);
  },
  getPolicyConfiguration(policyId: string): Promise<PulsePolicyConfigurationResponse> {
    return apiClient.get(`/entity-pulse/policies/${encodeURIComponent(policyId)}/configuration`);
  },
  /** One policy's summary, or (policyId null) all policies' together. */
  getPolicySummary(policyId: string | null, periodDays: PulsePolicyPeriodDays): Promise<PulsePolicySummaryResponse> {
    return apiClient.get('/entity-pulse/policies/summary', {
      params: { policy_id: policyId ?? undefined, period_days: periodDays },
    });
  },
  getEntityEvents(params: PulseEventsPageParams & { taxId: string; scope?: PulseEntityScope }): Promise<PulseEventsResponse> {
    return apiClient.get('/entity-pulse/entity-events', {
      params: { tax_id: params.taxId, scope: params.scope, ...toPulseEventsQuery(params) },
    });
  },
  /** A signed CSV link with every event of the entity's events tab (same scope, filters and order, all pages). */
  exportEntityEvents(
    params: PulseEventsFilters & { taxId: string; scope?: PulseEntityScope; sortField?: string; sortOrder?: 'asc' | 'desc' },
  ): Promise<CsvExportResponse> {
    return apiClient.get('/entity-pulse/entity-events/export', {
      params: {
        tax_id: params.taxId,
        scope: params.scope,
        sort_field: params.sortField,
        sort_order: params.sortOrder,
        ...toPulseEventsFilterQuery(params),
      },
    });
  },
  /** Counts and breakdowns of all the entity's events (direct and in other entities' monitoring). */
  getEntityEventCounts(taxId: string): Promise<PulseEntityEventCountsResponse> {
    return apiClient.get('/entity-pulse/entity-events/counts', { params: { tax_id: taxId } });
  },
  getEntityEventsSummary(taxId: string): Promise<PulseEventsSummary> {
    return apiClient.get('/entity-pulse/entity-events/summary', { params: { tax_id: taxId } });
  },
  getEntityEventsFilterOptions(taxId: string, scope: PulseEntityScope): Promise<PulseEventsFilterOptionsResponse> {
    return apiClient.get('/entity-pulse/entity-events/filter-options', {
      params: { tax_id: taxId, scope },
    });
  },
  getEntitySchedule(taxId: string): Promise<PulseEntityScheduleResponse> {
    return apiClient.get('/entity-pulse/entity-schedule', { params: { tax_id: taxId } });
  },
  /** Drop the entity's cached reads of one group: 'events' (event tabs) or 'runs' (run history, schedule,
   * last / next run). */
  clearEntityCache(taxId: string, group: 'events' | 'runs'): Promise<void> {
    return apiClient.post('/entity-pulse/entity/clear-cache', {}, { params: { tax_id: taxId, group } });
  },
  getMonitoringStatus(params: { taxId: string; entityType: 'company' | 'person' }): Promise<EntityMonitoringStatusResponse> {
    return apiClient.get('/entity-pulse/monitoring-status', {
      params: { tax_id: params.taxId, entity_type: params.entityType },
    });
  },
};

export default entityPulseApi;
